/**
 * @fileoverview Payment Pending page showing customers with outstanding payments.
 * Allows admins to view, resend, and edit payment links.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Badge
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Launch as LaunchIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Build as BuildIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  TrendingUp as TrendingUpIcon,
  CreditCard as CardIcon,
  Edit as EditIcon,
  Sms as SmsIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import api from '../../services/api';

/**
 * PaymentPending Component - Displays list of pending customer payments.
 *
 * Features:
 * - Summary cards (total pending, amount, urgent, expired)
 * - Table with customer/vehicle info, amount, status, urgency indicators
 * - Actions: view details, open payment link, email/call customer, resend link, edit quote
 * - Detail modal showing full payment breakdown
 *
 * Urgency levels:
 * - Green: < 3 days pending
 * - Warning: 3-6 days pending
 * - Error: 7+ days pending or expired
 *
 * @returns {JSX.Element} The payment pending page
 */
const PaymentPending = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({});
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [resendingPaymentId, setResendingPaymentId] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const user = useSelector(state => state.auth.user);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/payment-pending/');
      setPayments(response.data.payments);
      setSummary(response.data.summary);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch pending payments');
    } finally {
      setLoading(false);
    }
  };

  const handleResendPaymentLink = async (paymentId, customerName) => {
    if (!window.confirm(`Resend payment link to ${customerName}? This will generate a new link with 7 days expiration.`)) {
      return;
    }

    try {
      setResendingPaymentId(paymentId);
      const response = await api.post(`/v1/payments/${paymentId}/resend/`);

      if (response.data.success) {
        setSuccessMessage(`Payment link resent successfully to ${customerName}`);
        // Refresh the payment list to show updated expiration
        await fetchPendingPayments();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend payment link');
    } finally {
      setResendingPaymentId(null);
    }
  };

  const handleEditQuote = async (paymentId, customerName) => {
    if (!window.confirm(`Cancel this quote for ${customerName} and edit the pricing? The customer will receive a new payment link after you update the prices.`)) {
      return;
    }

    try {
      setEditingPaymentId(paymentId);
      const response = await api.post(`/v1/payments/${paymentId}/cancel-for-edit/`);

      if (response.data.success) {
        setSuccessMessage(`Quote cancelled for ${customerName}. Go to Admin Board to edit and resend.`);
        // Refresh the payment list - this payment will no longer appear
        await fetchPendingPayments();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel quote for editing');
    } finally {
      setEditingPaymentId(null);
    }
  };

  const handleSendSMS = async (payment) => {
    if (!payment.customer?.phone) {
      setError('Customer has no phone number');
      return;
    }

    try {
      // Start conversation with customer's phone
      const response = await api.post('/v1/conversations/conversations/start_conversation/', {
        phone_number: payment.customer.phone,
        contact_name: payment.customer.name,
      });

      // Store conversation ID and navigate to chat
      sessionStorage.setItem('chat:openConversationId', response.data.conversation.id);
      navigate('/chat');
    } catch (err) {
      setError('Error starting SMS conversation: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedPayment(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  /**
   * Get status chip for payment (pending or failed)
   */
  const getStatusChip = (payment) => {
    if (payment.status === 'failed') {
      return (
        <Chip
          label="Payment Failed"
          color="error"
          size="small"
          icon={<ErrorIcon />}
        />
      );
    }
    return (
      <Chip
        label="Awaiting Payment"
        color="warning"
        size="small"
        icon={<HourglassIcon />}
      />
    );
  };

  const getUrgencyChip = (payment) => {
    const { days_pending, is_expired } = payment;

    if (is_expired) {
      return <Chip label="EXPIRED" color="error" size="small" icon={<WarningIcon />} />;
    }

    if (days_pending >= 7) {
      return <Chip label={`${days_pending} days`} color="error" size="small" />;
    } else if (days_pending >= 3) {
      return <Chip label={`${days_pending} days`} color="warning" size="small" />;
    } else {
      return <Chip label={`${days_pending} days`} color="success" size="small" />;
    }
  };

  /**
   * Get user-friendly failure message from Stripe decline code
   */
  const getFailureMessage = (payment) => {
    const { failure_code, failure_reason } = payment;

    // Map common Stripe decline codes to user-friendly messages
    const declineCodeMessages = {
      'insufficient_funds': 'Insufficient funds on card',
      'card_declined': 'Card declined by bank',
      'expired_card': 'Card has expired',
      'incorrect_cvc': 'Incorrect security code (CVC)',
      'processing_error': 'Processing error - try again',
      'lost_card': 'Card reported lost',
      'stolen_card': 'Card reported stolen',
      'generic_decline': 'Card declined',
      'do_not_honor': 'Card declined by bank',
      'fraudulent': 'Card flagged as fraudulent',
    };

    if (failure_code && declineCodeMessages[failure_code]) {
      return declineCodeMessages[failure_code];
    }

    return failure_reason || 'Payment failed';
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'payment_assist':
        return '🏦';
      case 'stripe':
      default:
        return '💳';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Payment Pending Customers
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchPendingPayments}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 36, height: 36 }}>
                  <PaymentIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6">{summary.total_pending || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.main', mr: 1.5, width: 36, height: 36 }}>
                  <HourglassIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6">{summary.pending_count || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Awaiting
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'error.main', mr: 1.5, width: 36, height: 36 }}>
                  <ErrorIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6">{summary.failed_count || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'success.main', mr: 1.5, width: 36, height: 36 }}>
                  <CardIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6">{formatCurrency(summary.total_amount || 0)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'error.light', mr: 1.5, width: 36, height: 36 }}>
                  <WarningIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6">{summary.urgent_count || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Urgent
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'grey.500', mr: 1.5, width: 36, height: 36 }}>
                  <ScheduleIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6">{summary.expired_count || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Expired
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Payments Table */}
      {payments.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PaymentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No pending payments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All customers have completed their payments
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pending Payments ({payments.length})
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Pending</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow
                      key={payment.payment_id}
                      sx={{
                        backgroundColor: payment.status === 'failed' ? 'error.50' :
                                       payment.is_expired ? 'error.50' :
                                       payment.days_pending >= 7 ? 'warning.50' : 'inherit'
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {payment.customer.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {payment.customer.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {payment.vehicle.registration}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {payment.vehicle.make} {payment.vehicle.model}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(payment.amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {payment.job_request?.items_count || 0} items + labor
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box>
                          {getStatusChip(payment)}
                          {/* Show failure details if payment failed */}
                          {payment.status === 'failed' && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="error.main" display="block">
                                {getFailureMessage(payment)}
                              </Typography>
                              {payment.last_card_brand && payment.last_card_last4 && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {payment.last_card_brand} ****{payment.last_card_last4}
                                </Typography>
                              )}
                              {payment.attempt_count > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {payment.attempt_count} attempt{payment.attempt_count > 1 ? 's' : ''}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        {getUrgencyChip(payment)}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(payment.created_at)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(payment)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Open Payment Link">
                            <IconButton
                              size="small"
                              onClick={() => window.open(payment.payment_url, '_blank')}
                            >
                              <LaunchIcon />
                            </IconButton>
                          </Tooltip>

                          {payment.customer.email && (
                            <Tooltip title="Email Customer">
                              <IconButton
                                size="small"
                                onClick={() => window.location.href = `mailto:${payment.customer.email}`}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {payment.customer.phone && (
                            <Tooltip title="Call Customer">
                              <IconButton
                                size="small"
                                onClick={() => window.location.href = `tel:${payment.customer.phone}`}
                              >
                                <PhoneIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          {payment.customer.phone && (
                            <Tooltip title="Send SMS">
                              <IconButton
                                size="small"
                                onClick={() => handleSendSMS(payment)}
                                color="success"
                              >
                                <SmsIcon />
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip title={payment.is_expired ? "Resend Payment Link (Expired)" : "Resend Payment Link"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleResendPaymentLink(payment.payment_id, payment.customer.name)}
                                disabled={resendingPaymentId === payment.payment_id}
                                color={payment.is_expired ? "error" : "default"}
                              >
                                {resendingPaymentId === payment.payment_id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <RefreshIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Edit Quote - Cancel and revise pricing">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleEditQuote(payment.payment_id, payment.customer.name)}
                                disabled={editingPaymentId === payment.payment_id}
                                color="warning"
                              >
                                {editingPaymentId === payment.payment_id ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <EditIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Payment Details Modal */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Payment Details</Typography>
            <Box display="flex" gap={1}>
              {selectedPayment && getStatusChip(selectedPayment)}
              {selectedPayment && getUrgencyChip(selectedPayment)}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedPayment && (
            <Grid container spacing={3}>
              {/* Customer Info */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Customer Information
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Name:</strong> {selectedPayment.customer.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {selectedPayment.customer.email}
                    </Typography>
                    {selectedPayment.customer.phone && (
                      <Typography variant="body2">
                        <strong>Phone:</strong> {selectedPayment.customer.phone}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Vehicle & Booking Info */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Vehicle & Booking
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Vehicle:</strong> {selectedPayment.vehicle.make} {selectedPayment.vehicle.model} ({selectedPayment.vehicle.registration})
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Service Date:</strong> {selectedPayment.booking.booking_date ? formatDate(selectedPayment.booking.booking_date) : 'TBD'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Original Cost:</strong> {formatCurrency(selectedPayment.booking.original_cost)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Items Breakdown */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Items Breakdown
                    </Typography>
                    <List dense>
                      {selectedPayment.items.map((item, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`${item.quantity}x ${item.name}`}
                            secondary={`${formatCurrency(item.price)} each • Priority: ${item.priority}`}
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(item.total)}
                          </Typography>
                        </ListItem>
                      ))}

                      {selectedPayment.job_request.labor_cost > 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Labor Cost"
                            secondary="Required service labor"
                          />
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(selectedPayment.job_request.labor_cost)}
                          </Typography>
                        </ListItem>
                      )}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6">Total Additional:</Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(selectedPayment.amount)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Failure Details - Only show if payment failed */}
              {selectedPayment.status === 'failed' && (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderColor: 'error.main', bgcolor: 'error.50' }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                        <ErrorIcon color="error" />
                        <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                          Payment Failed
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Reason:</strong> {getFailureMessage(selectedPayment)}
                      </Typography>
                      {selectedPayment.last_card_brand && selectedPayment.last_card_last4 && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Card Used:</strong> {selectedPayment.last_card_brand} ****{selectedPayment.last_card_last4}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Attempts:</strong> {selectedPayment.attempt_count || 0}
                      </Typography>
                      {selectedPayment.last_attempt_at && (
                        <Typography variant="body2">
                          <strong>Last Attempt:</strong> {new Date(selectedPayment.last_attempt_at).toLocaleString('en-GB')}
                        </Typography>
                      )}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          The customer tried to pay but their payment failed. You can resend the payment link so they can try again with a different card.
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Payment Info */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Payment Information
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Payment ID:</strong> {selectedPayment.payment_id}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Status:</strong> {selectedPayment.status === 'failed' ? 'Failed' : 'Awaiting Payment'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Method:</strong> {selectedPayment.payment_method === 'payment_assist' ? 'Payment Assist Finance' : 'Stripe Card Payment'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Created:</strong> {formatDate(selectedPayment.created_at)}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Expires:</strong> {formatDate(selectedPayment.expires_at)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Days Pending:</strong> {selectedPayment.days_pending}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
          {selectedPayment && (
            <>
              {selectedPayment.customer.phone && (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => {
                    handleSendSMS(selectedPayment);
                    handleCloseDetails();
                  }}
                  startIcon={<SmsIcon />}
                >
                  Send SMS
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                onClick={() => {
                  handleEditQuote(selectedPayment.payment_id, selectedPayment.customer.name);
                  handleCloseDetails();
                }}
                startIcon={<EditIcon />}
              >
                Edit Quote
              </Button>
              <Button
                variant="contained"
                onClick={() => window.open(selectedPayment.payment_url, '_blank')}
                startIcon={<LaunchIcon />}
              >
                Open Payment Link
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentPending;
